import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Routes, Route, NavLink } from "react-router-dom";
import { FiChevronDown } from "react-icons/fi";

import api from "../../../../services/api";

import FrameworkTShirt from "./FrameworkTShirt";
import Planning from "./Planning";
import FrameworkNNL from "./FrameworkNNL";
import FrameworkBudget from "./FrameworkBudget";
import FrameworkByModule from "./FrameworkByModule";
import Builder from "./builder";

const AVAILABLE_TABS = [
  {
    id: "nnl",
    label: "Now, Next, Later",
    description: "Prioritize feature development by focusing on tasks in the Now, Next, and Later stages.",
  },
  {
    id: "tshirt",
    label: "T-Shirt",
    description: "Estimate feature complexity and effort for better resource planning and decision-making.",
  },
  {
    id: "budget",
    label: "Budget",
    description: "Track costs, planned budget based on epics and features.",
  },
  {
    id: "by_module",
    label: "By Module",
    description: "Clarify the roadmap by sorting features by module.",
  },
  {
    id: "builder",
    label: "Builder",
    description: "Build the roadmap with the builder.",
  },
];

const Roadmap = ({ project, setProject }) => {
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    if (location.pathname.endsWith("/roadmap") && project.roadmap_tabs?.length) {
      navigate(`/project/${id}/roadmap/${project.roadmap_tabs[0]}`);
    }
  }, []);

  return (
    <div className="space-y-1 ">
      <nav className="flex items-center justify-between border-b border-gray-300">
        <div className="flex items-center">
          {project.roadmap_tabs?.map((tab, index) => {
            const tabInfo = AVAILABLE_TABS.find((t) => t.id === tab);
            return <TabItem key={index} href={tab} title={tabInfo?.label || tab} />;
          })}
        </div>
        <RoadmapMenu project={project} setProject={setProject} />
      </nav>

      <Routes>
        <Route exact path="nnl" element={<FrameworkNNL project={project} setProject={setProject} />} />
        <Route path="tshirt" element={<FrameworkTShirt project={project} setProject={setProject} />} />
        <Route path="planning" element={<Planning project={project} />} />
        <Route path="budget" element={<FrameworkBudget project={project} setProject={setProject} />} />
        <Route path="by_module" element={<FrameworkByModule project={project} setProject={setProject} />} />
        <Route path="builder" element={<Builder project={project} />} />
      </Routes>
    </div>
  );
};

const RoadmapMenu = ({ project, setProject }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleCheckboxChange = async (tab, checked) => {
    const updatedTabs = checked ? [...(project.roadmap_tabs || []), tab] : (project.roadmap_tabs || []).filter((t) => t !== tab);
    await api.put(`/project/${project._id}`, {
      roadmap_tabs: updatedTabs,
    });
    setProject({ ...project, roadmap_tabs: updatedTabs });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const menu = document.getElementById("roadmap-menu");
      if (menu && !menu.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" id="roadmap-menu">
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="px-4 py-2 text-sm hover:bg-gray-100 rounded-t-lg flex items-center gap-2 shadow-sm transition-colors duration-200">
        <span className="font-medium text-gray-700">Customize Tabs</span>
        <FiChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isMenuOpen ? "rotate-180" : ""}`} />
      </button>

      {isMenuOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-lg border border-gray-100 z-10 overflow-hidden">
          <div className="p-2">
            {AVAILABLE_TABS.map((tab) => (
              <div key={tab.id} className="p-2 hover:bg-gray-50 rounded-lg transition-colors duration-150">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <div className="flex-shrink-0 mt-0.5">
                    <input
                      type="checkbox"
                      checked={project.roadmap_tabs?.includes(tab.id)}
                      onChange={(e) => handleCheckboxChange(tab.id, e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4 transition duration-150"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-900">{tab.label}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">{tab.description}</p>
                  </div>
                </label>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const TabItem = ({ href, title, index }) => {
  const { id } = useParams();
  return (
    <NavLink
      key={index}
      to={`/project/${id}/roadmap${href ? `/${href}` : ""}`}
      end
      className={({ isActive }) =>
        `text-sm px-4 py-2 hover:bg-gray-100 transition-colors border-b-2 ${isActive ? "border-sky-700 text-sky-700" : "border-transparent text-gray-500"}`
      }>
      {title}
    </NavLink>
  );
};

export default Roadmap;
